import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import Layout from '../../component/layout'
import { toastify } from '../../../../utils/toast';
import { getItem } from '../../../../utils/localStorage'
import { makeRequest2 } from '../../../../utils/makeRequest';
import { 
    get_rent_roll_report, 
    get_lease_expiry_pipeline, 
    get_rent_escalation_tracker, 
    get_unbilled_report 
} from '../../../../utils/urls';

function FacilityLeaseReports() {
    // State for each report tab
    const [rentRollData, setRentRollData] = useState([]);
    const [expiryData, setExpiryData] = useState([]);
    const [escalationData, setEscalationData] = useState([]);
    const [unbilledData, setUnbilledData] = useState([]);
    
    // Loading states
    const [loadingRentRoll, setLoadingRentRoll] = useState(false);
    const [loadingExpiry, setLoadingExpiry] = useState(false);
    const [loadingEscalation, setLoadingEscalation] = useState(false);
    const [loadingUnbilled, setLoadingUnbilled] = useState(false);

    // Filter states for Rent Roll
    const [rentRollFilters, setRentRollFilters] = useState({
        search: '',
        status: 'All Status',
        date: ''
    });

    // Filter states for Expiry Pipeline
    const [expiryFilters, setExpiryFilters] = useState({
        search: '',
        timeframe: 'Next 90 Days',
        urgency: 'All Urgency'
    });

    // Filter states for Escalation
    const [escalationFilters, setEscalationFilters] = useState({
        search: '',
        timeframe: 'Next 6 Months',
        status: 'All Status',
        type: 'All Types'
    });

    // Filter states for Unbilled
    const [unbilledFilters, setUnbilledFilters] = useState({
        search: '',
        issueType: 'All Issues',
        period: 'Current Month',
        severity: 'All Severity'
    });

    // Summary states
    const [rentRollSummary, setRentRollSummary] = useState({
        totalActiveLeases: 0,
        monthlyRentTotal: 0,
        totalDeposits: 0,
        avgRentPerUnit: 0
    });

    const [expirySummary, setExpirySummary] = useState({
        critical: 0,
        high: 0,
        medium: 0
    });

    const [escalationSummary, setEscalationSummary] = useState({
        pendingEscalations: 0,
        appliedThisYear: 0,
        totalRevenueIncrease: 0,
        avgIncreasePercent: 0
    });

    const [unbilledSummary, setUnbilledSummary] = useState({
        totalIssues: 0,
        notBilled: 0,
        underBilled: 0,
        revenueShortfall: 0
    });

    // Fetch Rent Roll Report
    const fetchRentRollReport = async () => {
        try {
            setLoadingRentRoll(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams();
            if (rentRollFilters.search) params.append('search', rentRollFilters.search);
            if (rentRollFilters.status !== 'All Status') params.append('status', rentRollFilters.status);
            if (rentRollFilters.date) params.append('date', rentRollFilters.date);

            const response = await makeRequest2(
                `${get_rent_roll_report}/${facilityId}?${params.toString()}`, 
                'GET', 
                {}
            );

            if (response.success) {
                setRentRollData(response.data.data.leases || []);
                setRentRollSummary(response.data.data.summary || {
                    totalActiveLeases: 0,
                    monthlyRentTotal: 0,
                    totalDeposits: 0,
                    avgRentPerUnit: 0
                });
            } else {
                throw new Error(response.message || 'Failed to fetch rent roll report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setLoadingRentRoll(false);
        }
    };

    // Fetch Lease Expiry Pipeline
    const fetchExpiryPipeline = async () => {
        try {
            setLoadingExpiry(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams();
            if (expiryFilters.search) params.append('search', expiryFilters.search);
            params.append('timeframe', expiryFilters.timeframe);
            if (expiryFilters.urgency !== 'All Urgency') params.append('urgency', expiryFilters.urgency);

            const response = await makeRequest2(
                `${get_lease_expiry_pipeline}/${facilityId}?${params.toString()}`, 
                'GET', 
                {}
            );

            if (response.success) {
                setExpiryData(response.data.data.leases || []);
                setExpirySummary(response.data.data.urgencyCounts || {
                    critical: 0,
                    high: 0,
                    medium: 0
                });
            } else {
                throw new Error(response.message || 'Failed to fetch expiry pipeline');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setLoadingExpiry(false);
        }
    };

    // Fetch Rent Escalation Tracker
    const fetchEscalationTracker = async () => {
        try {
            setLoadingEscalation(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams();
            if (escalationFilters.search) params.append('search', escalationFilters.search);
            params.append('timeframe', escalationFilters.timeframe);
            if (escalationFilters.status !== 'All Status') params.append('status', escalationFilters.status);
            if (escalationFilters.type !== 'All Types') params.append('type', escalationFilters.type);

            const response = await makeRequest2(
                `${get_rent_escalation_tracker}/${facilityId}?${params.toString()}`, 
                'GET', 
                {}
            );

            if (response.success) {
                setEscalationData(response.data.data.escalations || []);
                setEscalationSummary(response.data.data.summary || {
                    pendingEscalations: 0,
                    appliedThisYear: 0,
                    totalRevenueIncrease: 0,
                    avgIncreasePercent: 0
                });
            } else {
                throw new Error(response.message || 'Failed to fetch escalation tracker');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setLoadingEscalation(false);
        }
    };

    // Fetch Unbilled Report
    const fetchUnbilledReport = async () => {
        try {
            setLoadingUnbilled(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams();
            if (unbilledFilters.search) params.append('search', unbilledFilters.search);
            if (unbilledFilters.issueType !== 'All Issues') params.append('issueType', unbilledFilters.issueType);
            params.append('period', unbilledFilters.period);
            if (unbilledFilters.severity !== 'All Severity') params.append('severity', unbilledFilters.severity);

            const response = await makeRequest2(
                `${get_unbilled_report}/${facilityId}?${params.toString()}`, 
                'GET', 
                {}
            );

            if (response.success) {
                setUnbilledData(response.data.data.issues || []);
                setUnbilledSummary(response.data.data.summary || {
                    totalIssues: 0,
                    notBilled: 0,
                    underBilled: 0,
                    revenueShortfall: 0
                });
            } else {
                throw new Error(response.message || 'Failed to fetch unbilled report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setLoadingUnbilled(false);
        }
    };

    // Load initial data on component mount
    useEffect(() => {
        fetchRentRollReport();
        fetchExpiryPipeline();
        fetchEscalationTracker();
        fetchUnbilledReport();
    }, []);

    const header = (
        <div className="flex align-items-center justify-content-end gap-2">
            <Button type="button" label="CSV" rounded onClick={() => { }} data-pr-tooltip="CSV" />
            <Button type="button" label="XLS" severity="success" rounded onClick={() => { }} data-pr-tooltip="XLS" />
            <Button type="button" label="PDF" severity="warning" rounded onClick={() => { }} data-pr-tooltip="PDF" />
        </div>
    );

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/facility/"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>Lease Management</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"/facility/lease_management/reports"}>Reports</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row">
                                        <div className="col-sm-12">
                                            <div className="card">
                                                <div className="card-body py-0">
                                                    <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                                                        <li className="nav-item">
                                                            <a className="nav-link active" id="rent-roll-tab" data-bs-toggle="tab" href="#rent-roll" role="tab" aria-selected="true">
                                                                <i className="ti ti-list-details me-2"></i>Rent Roll
                                                            </a>
                                                        </li>
                                                        <li className="nav-item">
                                                            <a className="nav-link" id="expiry-tab" data-bs-toggle="tab" href="#expiry" role="tab" aria-selected="false">
                                                                <i className="ti ti-calendar-time me-2"></i>Lease Expiry Pipeline
                                                            </a>
                                                        </li>
                                                        <li className="nav-item">
                                                            <a className="nav-link" id="escalation-tab" data-bs-toggle="tab" href="#escalation" role="tab" aria-selected="false">
                                                                <i className="ti ti-trending-up me-2"></i>Rent Escalation Tracker
                                                            </a>
                                                        </li>
                                                        <li className="nav-item">
                                                            <a className="nav-link" id="unbilled-tab" data-bs-toggle="tab" href="#unbilled" role="tab" aria-selected="false">
                                                                <i className="ti ti-alert-circle me-2"></i>Unbilled/Under-Billed
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>

                                            <div className="tab-content">
                                                {/* 1. RENT ROLL REPORT */}
                                                <div className="tab-pane show active" id="rent-roll" role="tabpanel" aria-labelledby="rent-roll-tab">
                                                    <div className="row">
                                                        <div className="col-lg-12">
                                                            <div className="card">
                                                                <div className="card-header">
                                                                    <h5>Rent Roll Report</h5>
                                                                    <small className="text-muted">Complete overview of all active leases, monthly rent, and lease terms</small>
                                                                    <div className="row mt-3">
                                                                        <div className="col-md-3">
                                                                            <input 
                                                                                className="form-control" 
                                                                                placeholder="Search tenant, unit..." 
                                                                                value={rentRollFilters.search} 
                                                                                onChange={(e) => setRentRollFilters({...rentRollFilters, search: e.target.value})} 
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <select 
                                                                                className="form-control"
                                                                                value={rentRollFilters.status}
                                                                                onChange={(e) => setRentRollFilters({...rentRollFilters, status: e.target.value})}
                                                                            >
                                                                                <option>All Status</option>
                                                                                <option>Active</option>
                                                                                <option>Pending</option>
                                                                                <option>Expired</option>
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <input 
                                                                                className="form-control" 
                                                                                type="date" 
                                                                                value={rentRollFilters.date}
                                                                                onChange={(e) => setRentRollFilters({...rentRollFilters, date: e.target.value})}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <button 
                                                                                className="btn btn-primary w-100"
                                                                                onClick={fetchRentRollReport}
                                                                                disabled={loadingRentRoll}
                                                                            >
                                                                                <i className="ti ti-refresh me-2"></i>
                                                                                {loadingRentRoll ? 'Loading...' : 'Generate Report'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="card-body">
                                                                    {/* Summary Cards */}
                                                                    <div className="row mb-3">
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-primary">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Total Active Leases</h6>
                                                                                    <h4 className="mb-0">{rentRollSummary.totalActiveLeases}</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-success">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Monthly Rent Total</h6>
                                                                                    <h4 className="mb-0">KSh {rentRollSummary.monthlyRentTotal?.toLocaleString()}</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-info">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Total Deposits Held</h6>
                                                                                    <h4 className="mb-0">KSh {rentRollSummary.totalDeposits?.toLocaleString()}</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-warning">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Avg Rent Per Unit</h6>
                                                                                    <h4 className="mb-0">KSh {rentRollSummary.avgRentPerUnit?.toLocaleString()}</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <DataTable 
                                                                        value={rentRollData} 
                                                                        header={header} 
                                                                        emptyMessage="No active leases found." 
                                                                        sortMode="multiple" 
                                                                        paginator 
                                                                        rows={10} 
                                                                        stripedRows 
                                                                        tableStyle={{ minWidth: '50rem' }}
                                                                        loading={loadingRentRoll}
                                                                    >
                                                                        <Column field="unitNumber" sortable header="Unit #"></Column>
                                                                        <Column field="floor" header="Floor"></Column>
                                                                        <Column field="tenant" sortable header="Tenant"></Column>
                                                                        <Column field="tenantContact" header="Contact"></Column>
                                                                        <Column field="monthlyRent" sortable header="Monthly Rent" body={(rowData) => `KSh ${rowData.monthlyRent?.toLocaleString()}`}></Column>
                                                                        <Column field="securityDeposit" header="Deposit" body={(rowData) => `KSh ${rowData.securityDeposit?.toLocaleString()}`}></Column>
                                                                        <Column field="leaseStart" sortable header="Lease Start" body={(rowData) => new Date(rowData.leaseStart).toLocaleDateString()}></Column>
                                                                        <Column field="leaseEnd" sortable header="Lease End" body={(rowData) => new Date(rowData.leaseEnd).toLocaleDateString()}></Column>
                                                                        <Column field="duration" header="Duration (months)"></Column>
                                                                        <Column field="paymentDueDate" header="Due Date"></Column>
                                                                        <Column field="autoRenewal" header="Auto Renewal"></Column>
                                                                    </DataTable>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 2. LEASE EXPIRY/RENEWAL PIPELINE */}
                                                <div className="tab-pane" id="expiry" role="tabpanel" aria-labelledby="expiry-tab">
                                                    <div className="row">
                                                        <div className="col-lg-12">
                                                            <div className="card">
                                                                <div className="card-header">
                                                                    <h5>Lease Expiry/Renewal Pipeline</h5>
                                                                    <small className="text-muted">Track leases expiring soon and renewal actions needed</small>
                                                                    <div className="row mt-3">
                                                                        <div className="col-md-3">
                                                                            <input 
                                                                                className="form-control" 
                                                                                placeholder="Search tenant, unit..." 
                                                                                value={expiryFilters.search} 
                                                                                onChange={(e) => setExpiryFilters({...expiryFilters, search: e.target.value})} 
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <select 
                                                                                className="form-control"
                                                                                value={expiryFilters.timeframe}
                                                                                onChange={(e) => setExpiryFilters({...expiryFilters, timeframe: e.target.value})}
                                                                            >
                                                                                <option>Next 90 Days</option>
                                                                                <option>Next 30 Days</option>
                                                                                <option>Next 60 Days</option>
                                                                                <option>Next 120 Days</option>
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <select 
                                                                                className="form-control"
                                                                                value={expiryFilters.urgency}
                                                                                onChange={(e) => setExpiryFilters({...expiryFilters, urgency: e.target.value})}
                                                                            >
                                                                                <option>All Urgency</option>
                                                                                <option>Critical</option>
                                                                                <option>High</option>
                                                                                <option>Medium</option>
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <button 
                                                                                className="btn btn-primary w-100"
                                                                                onClick={fetchExpiryPipeline}
                                                                                disabled={loadingExpiry}
                                                                            >
                                                                                <i className="ti ti-refresh me-2"></i>
                                                                                {loadingExpiry ? 'Loading...' : 'Generate Report'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="card-body">
                                                                    {/* Alert Summary */}
                                                                    <div className="row mb-3">
                                                                        <div className="col-md-4">
                                                                            <div className="alert alert-danger">
                                                                                <h6 className="alert-heading">Critical (≤30 Days)</h6>
                                                                                <h4 className="mb-0">{expirySummary.critical} Leases</h4>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-4">
                                                                            <div className="alert alert-warning">
                                                                                <h6 className="alert-heading">High (31-60 Days)</h6>
                                                                                <h4 className="mb-0">{expirySummary.high} Leases</h4>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-4">
                                                                            <div className="alert alert-info">
                                                                                <h6 className="alert-heading">Medium (61-90 Days)</h6>
                                                                                <h4 className="mb-0">{expirySummary.medium} Leases</h4>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <DataTable 
                                                                        value={expiryData} 
                                                                        header={header} 
                                                                        emptyMessage="No expiring leases found." 
                                                                        sortMode="multiple" 
                                                                        paginator 
                                                                        rows={10} 
                                                                        stripedRows 
                                                                        tableStyle={{ minWidth: '50rem' }}
                                                                        loading={loadingExpiry}
                                                                    >
                                                                        <Column field="urgency" sortable header="Urgency" body={(rowData) => (
                                                                            <span className={`badge ${rowData.urgency === 'Critical' ? 'bg-danger text-light' : rowData.urgency === 'High' ? 'bg-warning text-light' : 'bg-info text-light'}`}>
                                                                                {rowData.urgency}
                                                                            </span>
                                                                        )}></Column>
                                                                        <Column field="unitNumber" sortable header="Unit #"></Column>
                                                                        <Column field="tenant" sortable header="Tenant"></Column>
                                                                        <Column field="tenantPhone" header="Phone"></Column>
                                                                        <Column field="leaseEnd" sortable header="Expiry Date" body={(rowData) => new Date(rowData.leaseEnd).toLocaleDateString()}></Column>
                                                                        <Column field="daysRemaining" sortable header="Days Left"></Column>
                                                                        <Column field="monthlyRent" header="Monthly Rent" body={(rowData) => `KSh ${rowData.monthlyRent?.toLocaleString()}`}></Column>
                                                                        <Column field="autoRenewal" header="Auto Renewal"></Column>
                                                                    </DataTable>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 3. RENT ESCALATION TRACKER */}
                                                <div className="tab-pane" id="escalation" role="tabpanel" aria-labelledby="escalation-tab">
                                                    <div className="row">
                                                        <div className="col-lg-12">
                                                            <div className="card">
                                                                <div className="card-header">
                                                                    <h5>Rent Escalation Tracker</h5>
                                                                    <small className="text-muted">Monitor upcoming and applied rent increases</small>
                                                                    <div className="row mt-3">
                                                                        <div className="col-md-2">
                                                                            <input 
                                                                                className="form-control" 
                                                                                placeholder="Search tenant, unit..." 
                                                                                value={escalationFilters.search} 
                                                                                onChange={(e) => setEscalationFilters({...escalationFilters, search: e.target.value})} 
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <select 
                                                                                className="form-control"
                                                                                value={escalationFilters.timeframe}
                                                                                onChange={(e) => setEscalationFilters({...escalationFilters, timeframe: e.target.value})}
                                                                            >
                                                                                <option>Next 6 Months</option>
                                                                                <option>Next 3 Months</option>
                                                                                <option>Next 12 Months</option>
                                                                                <option>All Upcoming</option>
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <select 
                                                                                className="form-control"
                                                                                value={escalationFilters.status}
                                                                                onChange={(e) => setEscalationFilters({...escalationFilters, status: e.target.value})}
                                                                            >
                                                                                <option>All Status</option>
                                                                                <option>Scheduled</option>
                                                                                <option>Applied</option>
                                                                                <option>Cancelled</option>
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <select 
                                                                                className="form-control"
                                                                                value={escalationFilters.type}
                                                                                onChange={(e) => setEscalationFilters({...escalationFilters, type: e.target.value})}
                                                                            >
                                                                                <option>All Types</option>
                                                                                <option>Percentage</option>
                                                                                <option>Fixed Amount</option>
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <button 
                                                                                className="btn btn-primary w-100"
                                                                                onClick={fetchEscalationTracker}
                                                                                disabled={loadingEscalation}
                                                                            >
                                                                                <i className="ti ti-refresh me-2"></i>
                                                                                {loadingEscalation ? 'Loading...' : 'Generate'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="card-body">
                                                                    {/* Summary Cards */}
                                                                    <div className="row mb-3">
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-primary">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Pending Escalations</h6>
                                                                                    <h4 className="mb-0">{escalationSummary.pendingEscalations}</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-success">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Applied This Year</h6>
                                                                                    <h4 className="mb-0">{escalationSummary.appliedThisYear}</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-info">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Total Revenue Increase</h6>
                                                                                    <h4 className="mb-0">KSh {escalationSummary.totalRevenueIncrease?.toLocaleString()}</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-warning">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Avg Increase %</h6>
                                                                                    <h4 className="mb-0">{escalationSummary.avgIncreasePercent}%</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <DataTable 
                                                                        value={escalationData} 
                                                                        header={header} 
                                                                        emptyMessage="No escalations found." 
                                                                        sortMode="multiple" 
                                                                        paginator 
                                                                        rows={10} 
                                                                        stripedRows 
                                                                        tableStyle={{ minWidth: '50rem' }}
                                                                        loading={loadingEscalation}
                                                                    >
                                                                        <Column field="status" sortable header="Status" body={(rowData) => (
                                                                            <span className={`badge ${rowData.status === 'scheduled' ? 'bg-warning text-light' : rowData.status === 'applied' ? 'bg-success text-light' : 'bg-secondary text-light'}`}>
                                                                                {rowData.status?.toUpperCase()}
                                                                            </span>
                                                                        )}></Column>
                                                                        <Column field="unitNumber" sortable header="Unit #"></Column>
                                                                        <Column field="tenant" sortable header="Tenant"></Column>
                                                                        <Column field="currentRent" sortable header="Current Rent" body={(rowData) => `KSh ${rowData.currentRent?.toLocaleString()}`}></Column>
                                                                        <Column field="escalationType" header="Type"></Column>
                                                                        <Column field="escalationValue" header="Escalation Value"></Column>
                                                                        <Column field="newRent" sortable header="New Rent" body={(rowData) => `KSh ${rowData.newRent?.toLocaleString()}`}></Column>
                                                                        <Column field="increase" header="Increase" body={(rowData) => `KSh ${rowData.increase?.toLocaleString()}`}></Column>
                                                                        <Column field="increasePercent" sortable header="Increase %" body={(rowData) => `${rowData.increasePercent}%`}></Column>
                                                                        <Column field="effectiveDate" sortable header="Effective Date" body={(rowData) => new Date(rowData.effectiveDate).toLocaleDateString()}></Column>
                                                                        <Column field="daysUntil" sortable header="Days Until"></Column>
                                                                    </DataTable>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 4. UNBILLED/UNDER-BILLED REPORT */}
                                                <div className="tab-pane" id="unbilled" role="tabpanel" aria-labelledby="unbilled-tab">
                                                    <div className="row">
                                                        <div className="col-lg-12">
                                                            <div className="card">
                                                                <div className="card-header">
                                                                    <h5>Unbilled/Under-Billed Report</h5>
                                                                    <small className="text-muted">Identify billing discrepancies and revenue leakage</small>
                                                                    <div className="row mt-3">
                                                                        <div className="col-md-3">
                                                                            <input 
                                                                                className="form-control" 
                                                                                placeholder="Search tenant, unit..." 
                                                                                value={unbilledFilters.search} 
                                                                                onChange={(e) => setUnbilledFilters({...unbilledFilters, search: e.target.value})} 
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <select 
                                                                                className="form-control"
                                                                                value={unbilledFilters.issueType}
                                                                                onChange={(e) => setUnbilledFilters({...unbilledFilters, issueType: e.target.value})}
                                                                            >
                                                                                <option>All Issues</option>
                                                                                <option>Not Billed</option>
                                                                                <option>Under-Billed</option>
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <select 
                                                                                className="form-control"
                                                                                value={unbilledFilters.period}
                                                                                onChange={(e) => setUnbilledFilters({...unbilledFilters, period: e.target.value})}
                                                                            >
                                                                                <option>Current Month</option>
                                                                                <option>Last Month</option>
                                                                                <option>Last 3 Months</option>
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            <select 
                                                                                className="form-control"
                                                                                value={unbilledFilters.severity}
                                                                                onChange={(e) => setUnbilledFilters({...unbilledFilters, severity: e.target.value})}
                                                                            >
                                                                                <option>All Severity</option>
                                                                                <option>Critical</option>
                                                                                <option>High</option>
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <button 
                                                                                className="btn btn-primary w-100"
                                                                                onClick={fetchUnbilledReport}
                                                                                disabled={loadingUnbilled}
                                                                            >
                                                                                <i className="ti ti-refresh me-2"></i>
                                                                                {loadingUnbilled ? 'Loading...' : 'Generate Report'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="card-body">
                                                                    {/* Summary Cards */}
                                                                    <div className="row mb-3">
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-danger">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Total Issues</h6>
                                                                                    <h4 className="mb-0">{unbilledSummary.totalIssues}</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-warning">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Not Billed</h6>
                                                                                    <h4 className="mb-0">{unbilledSummary.notBilled} Units</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-info">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Under-Billed</h6>
                                                                                    <h4 className="mb-0">{unbilledSummary.underBilled} Units</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <div className="card bg-light-danger">
                                                                                <div className="card-body">
                                                                                    <h6 className="mb-1">Revenue Shortfall</h6>
                                                                                    <h4 className="mb-0">KSh {unbilledSummary.revenueShortfall?.toLocaleString()}</h4>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <DataTable 
                                                                        value={unbilledData} 
                                                                        header={header} 
                                                                        emptyMessage="No billing issues found." 
                                                                        sortMode="multiple" 
                                                                        paginator 
                                                                        rows={10} 
                                                                        stripedRows 
                                                                        tableStyle={{ minWidth: '50rem' }}
                                                                        loading={loadingUnbilled}
                                                                    >
                                                                        <Column field="severity" sortable header="Severity" body={(rowData) => (
                                                                            <span className={`badge ${rowData.severity === 'Critical' ? 'bg-danger text-light' : 'bg-warning text-light'}`}>
                                                                                {rowData.severity}
                                                                            </span>
                                                                        )}></Column>
                                                                        <Column field="issue" sortable header="Issue Type" body={(rowData) => (
                                                                            <span className={`badge ${rowData.issue === 'Not Billed' ? 'bg-danger text-light' : 'bg-warning text-light'}`}>
                                                                                {rowData.issue}
                                                                            </span>
                                                                        )}></Column>
                                                                        <Column field="unitNumber" sortable header="Unit #"></Column>
                                                                        <Column field="tenant" sortable header="Tenant"></Column>
                                                                        <Column field="expectedRent" sortable header="Expected Rent" body={(rowData) => `KSh ${rowData.expectedRent?.toLocaleString()}`}></Column>
                                                                        <Column field="billedAmount" sortable header="Billed Amount" body={(rowData) => `KSh ${rowData.billedAmount?.toLocaleString()}`}></Column>
                                                                        <Column field="shortfall" sortable header="Shortfall" body={(rowData) => (
                                                                            <span className="text-danger fw-bold">
                                                                                KSh {rowData.shortfall?.toLocaleString()}
                                                                            </span>
                                                                        )}></Column>
                                                                        <Column field="yearMonth" header="Period"></Column>
                                                                        <Column field="invoiceNumber" header="Invoice #"></Column>
                                                                        <Column field="lastInvoiceDate" header="Last Invoice" body={(rowData) => 
                                                                            rowData.lastInvoiceDate === 'Never' ? 
                                                                            <span className="text-danger">Never</span> : 
                                                                            new Date(rowData.lastInvoiceDate).toLocaleDateString()
                                                                        }></Column>
                                                                    </DataTable>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default FacilityLeaseReports;